// static/js/history.js
import axios from 'axios';

/**
 * Fetches conversations from the API and populates all history lists.
 */
async function loadConversations() {
    const historyLists = document.querySelectorAll('.history-list');
    if (historyLists.length === 0) return;

    // Show loading indicator in all lists
    historyLists.forEach(list => {
        list.innerHTML = '<li class="nav-item"><span class="nav-link text-muted">Loading...</span></li>';
    });

    try {
        const response = await axios.get('/api/conversations');
        const conversations = response.data;

        // Populate each list independently
        historyLists.forEach(historyList => {
            historyList.innerHTML = ''; // Clear loading indicator

            if (!conversations || conversations.length === 0) {
                historyList.innerHTML = '<li class="nav-item"><span class="nav-link text-muted">No past conversations found.</span></li>';
                return;
            }

            conversations.forEach(convo => {
                const li = document.createElement('li');
                li.className = 'nav-item';

                let conversationId = convo.id || convo._id;
                if (typeof conversationId === 'object' && conversationId !== null && conversationId.$oid) {
                    conversationId = conversationId.$oid;
                }
                
                if (!conversationId) return;

                li.dataset.id = conversationId;

                const currentConversationId = new URLSearchParams(window.location.search).get('conversation_id');
                if (currentConversationId === conversationId) {
                    li.classList.add('active');
                }
                
                const sanitizedTitle = document.createElement('div');
                sanitizedTitle.textContent = convo.title;

                li.innerHTML = `
                    <a href="/chat?conversation_id=${conversationId}" class="nav-link history-link" title="${sanitizedTitle.textContent}">
                        <span class="history-title">${sanitizedTitle.innerHTML}</span>
                    </a>
                    <span class="history-icons">
                        <i class="fas fa-download download-icon" title="Download conversation"></i>
                        <i class="fas fa-pencil-alt rename-icon" title="Rename conversation"></i>
                        <i class="fas fa-trash delete-icon" title="Delete conversation"></i>
                    </span>
                `;
                historyList.appendChild(li);
            });
        });

    } catch (error) {
        console.error('Error loading conversation history:', error);
        historyLists.forEach(list => {
            list.innerHTML = '<li class="nav-item"><span class="nav-link text-danger">Error loading history.</span></li>';
        });
    }
}

/**
 * Initializes the conversation history feature.
 */
export function initHistory() {
    const historyLists = document.querySelectorAll('.history-list');
    if (historyLists.length > 0) {
        console.log("History module initialized.");
        loadConversations();

        // New Chat Buttons
        document.querySelectorAll('.new-chat-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const event = new CustomEvent('startnewchat');
                document.dispatchEvent(event);
            });
        });

        // Search/Filter logic
        const searchInputs = document.querySelectorAll('.history-search');
        searchInputs.forEach(input => {
            input.addEventListener('input', (e) => {
                const term = e.target.value.toLowerCase();
                // Filter all items in all history lists
                document.querySelectorAll('.history-list .nav-item').forEach(item => {
                    const titleSpan = item.querySelector('.history-title');
                    if (titleSpan) {
                        const title = titleSpan.textContent.toLowerCase();
                        if (title.includes(term)) {
                            item.style.setProperty('display', 'flex', 'important');
                        } else {
                            item.style.setProperty('display', 'none', 'important');
                        }
                    }
                });
                // Sync values of all search inputs
                searchInputs.forEach(otherInput => {
                    if (otherInput !== input) otherInput.value = e.target.value;
                });
            });
        });

        // Delegated event listeners for actions (works for all lists)
        document.addEventListener('click', async (e) => {
            // Delete
            if (e.target.classList.contains('delete-icon')) {
                const listItem = e.target.closest('.nav-item');
                const conversationId = listItem.dataset.id;

                if (confirm('Are you sure you want to delete this conversation?')) {
                    try {
                        const response = await axios.delete(`/api/conversations/${conversationId}`);
                        if (response.status === 204) {
                            const isActive = new URLSearchParams(window.location.search).get('conversation_id') === conversationId;
                            
                            // Remove from all lists
                            document.querySelectorAll(`.history-list .nav-item[data-id="${conversationId}"]`).forEach(el => el.remove());

                            if (isActive) {
                                document.dispatchEvent(new CustomEvent('startnewchat'));
                            }
                            
                            // Check empty state for all lists
                            document.querySelectorAll('.history-list').forEach(list => {
                                if (list.children.length === 0) {
                                    list.innerHTML = '<li class="nav-item"><span class="nav-link text-muted">No past conversations found.</span></li>';
                                }
                            });
                        }
                    } catch (error) {
                        console.error('Failed to delete conversation:', error);
                        alert('Failed to delete conversation.');
                    }
                }
            }

            // Rename
            if (e.target.classList.contains('rename-icon')) {
                const listItem = e.target.closest('.nav-item');
                const conversationId = listItem.dataset.id;
                const historyLink = listItem.querySelector('.history-link');
                const historyTitleSpan = listItem.querySelector('.history-title');
                const originalTitle = historyTitleSpan.textContent;

                historyLink.style.display = 'none';

                const input = document.createElement('input');
                input.type = 'text';
                input.className = 'form-control form-control-sm';
                input.value = originalTitle;
                listItem.insertBefore(input, historyLink.nextSibling);
                input.focus();

                const saveRename = async () => {
                    const newTitle = input.value.trim();
                    if (newTitle && newTitle !== originalTitle) {
                        try {
                            await axios.put(`/api/conversations/${conversationId}`, { title: newTitle });
                            // Update all UI instances
                            document.querySelectorAll(`.history-list .nav-item[data-id="${conversationId}"]`).forEach(li => {
                                const link = li.querySelector('.history-link');
                                const titleSpan = li.querySelector('.history-title');
                                if (titleSpan) titleSpan.textContent = newTitle;
                                if (link) link.title = newTitle;
                            });
                        } catch (error) {
                            console.error('Failed to rename conversation:', error);
                            alert('Failed to rename conversation.');
                        }
                    }
                    input.remove();
                    historyLink.style.display = '';
                };

                input.addEventListener('blur', saveRename);
                input.addEventListener('keydown', (ev) => {
                    if (ev.key === 'Enter') {
                        input.removeEventListener('blur', saveRename);
                        saveRename();
                    } else if (ev.key === 'Escape') {
                        input.removeEventListener('blur', saveRename);
                        input.remove();
                        historyLink.style.display = '';
                    }
                });
            }

            // Download
            if (e.target.classList.contains('download-icon')) {
                const listItem = e.target.closest('.nav-item');
                const conversationId = listItem.dataset.id;
                window.location.href = `/api/conversations/${conversationId}/download`;
            }
        });

        document.addEventListener('conversationcreated', () => {
            console.log('New conversation created, refreshing history list...');
            loadConversations();
        });
    }
}
